import { z } from 'zod';

/**
 * The breakdown sheet, as a closed vocabulary.
 *
 * TAKEN FROM THE SPEC, NOT INVENTED. The full list is
 * `Production_Script_Breakdown_Colour_Tags.pdf` (design pack) — the industry
 * reference a first AD tags against — and the display subset and its colours
 * are `15 Script Review`'s own `bdCats()`. An earlier draft of this file made
 * up a plausible-sounding set instead, which is how you end up with a
 * "Wardrobe" the designs call Costumes and a Weapons category nobody
 * implemented.
 *
 * It is an enum rather than free text because `element.category` and
 * `production_element.category` are plain varchars: nothing stopped one writer
 * saying `PROP` and another `PROPS`, after which the props list is silently two
 * lists and neither is complete.
 *
 * CAST is load-bearing beyond tagging. `stripboard.service` builds Day Out of
 * Days from `production_element` filtered to this exact value, and a DOOD is a
 * document performers are paid against — so the string is a contract.
 */
export const ElementCategory = z.enum([
  // The sixteen the Script Review artboard shows, in its order.
  'CAST',
  'BACKGROUND',
  'STUNTS',
  'PROPS',
  'SET_DRESSING',
  'COSTUMES',
  'HAIR_MAKEUP',
  'SFX',
  'VFX',
  'PICTURE_VEHICLES',
  'ANIMALS',
  'SPECIAL_EQUIPMENT',
  'WEAPONS',
  'GRAPHICS_PLAYBACK',
  'CHILDREN_MINORS',
  'SPECIAL_CREW',

  /** Camera, grip and lighting package for the scene — "Alexa 35 + primes",
   *  "Dolly + track", "2× M18 HMI". NOT in the industry colour-tag reference,
   *  which stops at SPECIAL EQUIPMENT (cranes, underwater rigs); this is the
   *  standard package a scene needs, and `13 Project Workspace v2` tags scenes
   *  with it. Kept separate from SPECIAL_EQUIPMENT so "what do we always carry"
   *  and "what does this scene need specially" stay different questions. */
  'EQUIPMENT',

  // The rest of the reference. Not shown by default, but representable — a
  // production that needs an intimacy coordinator must be able to say so
  // without inventing a category.
  'ANIMAL_WRANGLERS',
  'SECURITY_POLICE_FIRE',
  'SPECIAL_MAKEUP_PROSTHETICS',
  'GREEN_BLUE_SCREEN',
  'SOUND_PLAYBACK',
  'MECHANICAL_EFFECTS',
  'INTIMACY_NUDITY',
]);
export type ElementCategory = z.infer<typeof ElementCategory>;

export interface ElementCategoryPresentation {
  readonly label: string;
  /** The colour tag. Separation matters more than the exact hue — the
   *  reference says so — but these are the artboard's values. */
  readonly colour: string;
  /** What belongs here, from the reference's "breakdown use" column. Shown as
   *  help, because Props vs Set dressing is a real question on a real sheet. */
  readonly use: string;
}

export const ELEMENT_CATEGORIES: Readonly<
  Record<ElementCategory, ElementCategoryPresentation>
> = Object.freeze({
  CAST: { label: 'Cast', colour: '#D43F3F', use: 'Speaking actors and principal performers' },
  BACKGROUND: {
    label: 'Background / extras',
    colour: '#EAB308',
    use: 'Atmosphere, crowds and featured extras',
  },
  STUNTS: {
    label: 'Stunts',
    colour: '#F97316',
    use: 'Stunt performers, fights, falls and stunt driving',
  },
  PROPS: {
    label: 'Props',
    colour: '#7C3AED',
    use: 'Hand and action props: phones, letters, bags. Handled by performers',
  },
  SET_DRESSING: {
    label: 'Set dressing',
    colour: '#0E7490',
    use: 'Furniture, pictures, lamps — belongs to the environment, not the performer',
  },
  COSTUMES: {
    label: 'Costumes',
    colour: '#DB2777',
    use: 'Character-specific clothing, uniforms and specialty costumes',
  },
  HAIR_MAKEUP: {
    label: 'Hair & makeup',
    colour: '#A855F7',
    use: 'Special looks, wigs, period hair, tattoos, ageing and blood continuity',
  },
  SFX: {
    label: 'SFX',
    colour: '#B45309',
    use: 'Physically created on set: rain, smoke, fire, wind, breakaway glass',
  },
  VFX: {
    label: 'VFX',
    colour: '#2563EB',
    use: 'Created or altered digitally: screen replacement, set extensions, removals',
  },
  PICTURE_VEHICLES: {
    label: 'Picture vehicles',
    colour: '#16A34A',
    use: 'Vehicles visible in the finished shot. Crew transport is not an element',
  },
  ANIMALS: { label: 'Animals', colour: '#84CC16', use: 'Dogs, horses, livestock, trained animals' },
  SPECIAL_EQUIPMENT: {
    label: 'Special equipment',
    colour: '#475467',
    use: 'Crane, Steadicam, underwater rigs, process trailers, specialty camera rigs',
  },
  WEAPONS: {
    label: 'Weapons',
    colour: '#991B1B',
    use: 'Firearms, knives, swords and armourer requirements',
  },
  GRAPHICS_PLAYBACK: {
    label: 'Graphics / playback',
    colour: '#06B6D4',
    use: 'TVs, monitors, phones, computer screens and on-screen graphics',
  },
  CHILDREN_MINORS: {
    label: 'Children / minors',
    colour: '#0891B2',
    use: 'Child performers, guardians, tutors and restricted working hours',
  },
  SPECIAL_CREW: {
    label: 'Special crew',
    colour: '#6B7280',
    use: 'Divers, medics, marine crew, aerial unit and specialist technicians',
  },
  EQUIPMENT: {
    label: 'Equipment',
    colour: '#334155',
    use: 'Camera, grip and lighting package: bodies, lenses, dolly, track, HMIs',
  },

  ANIMAL_WRANGLERS: {
    label: 'Animal wranglers',
    colour: '#65A30D',
    use: 'Handlers and trainers required for animal work',
  },
  SECURITY_POLICE_FIRE: {
    label: 'Security / police / fire',
    colour: '#1D4ED8',
    use: 'Police support, fire safety, security and controlled-set requirements',
  },
  SPECIAL_MAKEUP_PROSTHETICS: {
    label: 'Special makeup / prosthetics',
    colour: '#C026D3',
    use: 'Wounds, prosthetics, creature appliances and specialty makeup',
  },
  GREEN_BLUE_SCREEN: {
    label: 'Green / blue screen',
    colour: '#14B8A6',
    use: 'Chroma-screen requirements',
  },
  SOUND_PLAYBACK: {
    label: 'Sound / playback',
    colour: '#0284C7',
    use: 'Music playback, practical audio and special sound cues',
  },
  MECHANICAL_EFFECTS: {
    label: 'Mechanical effects',
    colour: '#EA580C',
    use: 'Moving rigs, gimbals, mechanical props and collapsing devices',
  },
  INTIMACY_NUDITY: {
    label: 'Intimacy / nudity',
    colour: '#D8B4FE',
    use: 'Closed-set requirements, intimacy coordination and modesty garments',
  },
});

/**
 * What the breakdown panel shows, in the artboard's order.
 *
 * EMPTY CATEGORIES STAY VISIBLE — the design says so, and it is the whole point
 * of a breakdown sheet: an empty Stunts row is a statement that this scene has
 * none, which is different from nobody having looked. A panel that hid empty
 * rows would make "not yet tagged" and "nothing needed" identical.
 */
export const BREAKDOWN_PANEL_CATEGORIES: readonly ElementCategory[] = Object.freeze([
  'CAST',
  'BACKGROUND',
  'STUNTS',
  'PROPS',
  'SET_DRESSING',
  'COSTUMES',
  'HAIR_MAKEUP',
  'SFX',
  'VFX',
  'PICTURE_VEHICLES',
  'ANIMALS',
  'SPECIAL_EQUIPMENT',
  'WEAPONS',
  'GRAPHICS_PLAYBACK',
  'CHILDREN_MINORS',
  'SPECIAL_CREW',
  'EQUIPMENT',
]);

/**
 * Which categories a deterministic parse can honestly produce.
 *
 * A character cue in Fountain is STRUCTURE — an uppercase line above dialogue —
 * so CAST is read, not inferred. Every other category lives in action prose
 * ("she pockets the revolver") and needs a model that actually reads. Guessing
 * them from keywords produces plausible tags a producer would trust, which is
 * worse than producing none: an untagged prop is a gap someone fills, a
 * wrongly-tagged one is a gap nobody looks for.
 */
export const STRUCTURALLY_DERIVABLE_CATEGORIES: readonly ElementCategory[] = Object.freeze([
  'CAST',
]);

/**
 * One tagged occurrence of an element in one scene.
 *
 * Mirrors `SceneLocationSignal`: evidence first, and a band with a closed
 * reason rather than a number.
 */
export const SceneElementSignal = z.object({
  /** Verbatim from the scene. Re-located against the source before any
   *  highlight renders; the model's own offsets are never trusted. */
  evidenceQuote: z.string().min(1).max(2000),
  category: ElementCategory,
  /** As written in the script — "MIRA", "Island ferry". */
  rawName: z.string().min(1).max(255),
  /** Case- and article-folded, so MIRA and Mira are one element. */
  normalizedName: z.string().min(1).max(255),
  confidenceBand: z.enum(['HIGH', 'NEEDS_REVIEW', 'AMBIGUOUS']),
  confidenceReason: z.string().max(64).nullable().default(null),
});
export type SceneElementSignal = z.infer<typeof SceneElementSignal>;

/**
 * Folds the forms of a name that mean the same element.
 *
 * Character cues carry modifiers Fountain treats as syntax — `MIRA (CONT'D)`,
 * `MIRA (V.O.)`, `MIRA (O.S.)` — and a producer who sees three of those as
 * three cast members has a DOOD that pays three people for one part.
 */
/**
 * Ink that stays readable on a breakdown colour.
 *
 * The palette is the industry's, not ours, and it is not a text palette: ten of
 * the twenty-four are dark enough that near-black on them falls under a 4.5:1
 * contrast ratio, and Equipment (#334155) reaches 2.0 — a marked phrase in the
 * screenplay was a dark word on a dark block. The colour is what tells a first
 * AD what kind of element it is, so the colour stays and the INK moves.
 *
 * WHICHEVER OF THE TWO CONTRASTS MORE, not a luminance threshold. A threshold
 * looks equivalent and is not: Stunts (#F97316) sits at luminance 0.33, which
 * any plausible cut-off puts on the dark side, and white on it reaches 2.8
 * while near-black reaches 7.5. Comparing the two ratios is also provably
 * sufficient — they multiply to 21, so the better of them is never below
 * √21 ≈ 4.58, clearing 4.5:1 on any colour this palette can grow. The dark ink
 * is TRUE black rather than the body's #232323 for that reason: the near-black
 * costs about a fifth of the ratio, and Hair & makeup (#A855F7) fails on both
 * inks with it and passes on one without it.
 */
export function readableInkOn(colour: string): string {
  const hex = colour.replace('#', '');
  if (!/^[0-9a-fA-F]{6}$/.test(hex)) return '#000000';

  const channel = (pair: string): number => {
    const c = Number.parseInt(pair, 16) / 255;

    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  };

  const luminance =
    0.2126 * channel(hex.slice(0, 2)) +
    0.7152 * channel(hex.slice(2, 4)) +
    0.0722 * channel(hex.slice(4, 6));

  const againstBlack = (luminance + 0.05) / 0.05;
  const againstWhite = 1.05 / (luminance + 0.05);

  return againstWhite > againstBlack ? '#ffffff' : '#000000';
}

export function normalizeElementName(raw: string): string {
  return raw
    .replace(/\(.*?\)/g, ' ')
    .replace(/\b(CONT'D|CONTD|V\.?O\.?|O\.?S\.?|O\.?C\.?|PRE-?LAP)\b/gi, ' ')
    .replace(/^(THE|A|AN)\s+/i, '')
    .replace(/[^\p{L}\p{N}\s'-]/gu, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .toUpperCase();
}

/**
 * DOES THIS SCENE MENTION THIS ELEMENT — the rule behind "every scene it
 * appears in" (owner review 2026-10-27, decision D2).
 *
 * The scene's text contains the element's name as a WHOLE WORD (or whole
 * phrase), case-insensitively: "car" is in "the car stops" and not in "a
 * scarf" or "carpet". The words of a phrase may be split by any run of
 * whitespace, so a name that wraps onto the next line of a screenplay still
 * matches. Letters and digits of any script count as word characters, so the
 * rule holds for a Cyrillic or accented name as it does for ASCII.
 *
 * It is deliberately literal. It does not stem ("cars" is not "car"), does not
 * resolve pronouns and does not ask the model: the producer sees the count and
 * the scene list BEFORE anything is written, and a predictable rule is one they
 * can check at a glance. The narrower alternative — only the scenes where the
 * model already found the element — was the option not chosen.
 */
export function appearsInText(text: string | null | undefined, name: string): boolean {
  if (text === null || text === undefined) return false;

  const words = name.trim().split(/\s+/u).filter((word) => word !== '');
  if (words.length === 0) return false;

  const phrase = words.map((word) => word.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('\\s+');

  return new RegExp(`(?<![\\p{L}\\p{N}])${phrase}(?![\\p{L}\\p{N}])`, 'iu').test(text);
}
